import { ENTITY_TYPES } from '@sales-center/common'

export interface ApiError {
  Message: string
}

export interface ITemplate {
  branches: string[]
  category: string
  createdBy: string
  createdDate: string
  isAvailableOffline: boolean
  isSalesCenter: boolean
  modifiedDate: string
  status: string | null
  statusMessage: string | null
  templateFormName: string
  templateId: number
  templateStatus: string
  isCardCaptureOptional: boolean
}

export interface ITemplateDetail {
  appId: number
  branches: {
    id: number | null
    name: string
    parentId: number | null
  }[]
  categoryId: number
  data: FormManagerPdfData
  dynamicServiceBlock: boolean
  isAvailableOffline: boolean
  numOfServices: number
  pdfFromRepoUrl: string
  sendToCustomer: boolean
  templateName: string
  templateStatus: number
  willDynamicServiceBlockFit: boolean
  isCardCaptureEnabled: boolean
  isCardCaptureOptional: boolean
}

export interface IFormDetail {
  accountId: string | null
  createdBy: string
  createdDate: string
  data: FormManagerPdfData
  editUrl: string | null
  externalEntityId1: string | null
  externalEntityId2: string | null
  externalEntityId3: string | null
  externalEntityId4: string | null
  externalEntityId5: string | null
  externalEntityType1: string | null
  externalEntityType2: string | null
  externalEntityType3: string | null
  externalEntityType4: string | null
  externalEntityType5: string | null
  flattenPDFUrl: string | null
  formId: number
  formName: string
  formStatus: number
  isMandatory: boolean
  lastNotifiedBy: string | null
  lastNotifiedDate: string | null
  mobileGuid: string | null
  modifiedBy: string | null
  modifiedDate: string | null
  serviceLocationId: string | null
  serviceLocationName: string | null
  status: FM_FORM_STATUSES
  statusMessage: string | null
  templateId: number
  userId: string
  workOrderId: string | null
  workOrderNumber: any
}

export interface IForm {
  accountId: string | null
  accountName: string | null
  accountNumber: number | null
  attachments: any[]
  businessUnitId: string | null
  createdBy: string
  createdDate: string
  editUrl: string | null
  externalEntityId1: string | null
  externalEntityId2: string | null
  externalEntityId3: string | null
  externalEntityId4: string | null
  externalEntityId5: string | null
  externalEntityType1: string | null
  externalEntityType2: string | null
  externalEntityType3: string | null
  externalEntityType4: string | null
  externalEntityType5: string | null
  flattenedFiles: any[]
  formName: string
  history: FormHistoryItem[]
  id: number
  mobileGuid: string | null
  modifiedBy: string | null
  modifiedDate: string | null
  notifications: any[]
  serviceLocationId: string | null
  serviceLocationName: string | null
  status: FM_FORM_STATUSES
  templateCategory: string
  templateName: string
  templateNames: string[]
  workOrderId: string | null
  workOrderNumber: any
}

export interface FormHistoryItem {
  isCanceled: boolean
  isRevised: boolean
  modifiedBy: string
  modifiedDate: string
  status: FormStatusCode
}

export enum FM_FORM_STATUSES {
  All = 'ALL',
  Canceled = 'Canceled',
  Complete = 'Complete',
  Declined = 'Declined',
  Draft = 'Draft',
  InProgress = 'InProgress',
  OutForSignature = 'OutForSignature',
  Signed = 'Signed',
}

interface FormManagerPdfData {
  height: number
  imageUrl: string
  page: string
  pdfObjList: {
    calculations: any
    displayName: string
    fieldExportValue: string
    fieldFullName: string | null
    fieldId: string
    fieldName: string
    fieldType: string
    fieldValue: string
    height: number
    isCalculation: boolean
    isChecked: boolean
    isFormatted: boolean
    isImageFromCoreApp: boolean
    mappedData: {
      autoFill: boolean
      dataPoint: string | null
      datasource: any
      fieldType: string | null
      lookup: any
      permission: {
        id: string
        name: string
      }[]
    }[]
    maxLength: number
    multiLine: boolean
    optionList: any[]
    optionName: any
    page: number
    pdfHeight: number
    pdfWidth: number
    pdfX: number
    pdfY: number
    readOnly: boolean
    required: boolean
    tabOrder: number
    tooltip: string
    width: number
    x: number
    y: number
  }[]
  width: number
}

export interface GetTemplateDetailInput {
  id: number
  proposalEntityType: ENTITY_TYPES
  proposalEntityId: number
  dsbServiceIds?: number[]
}

export interface GetFormsInput {
  category?: string
  createdBy?: string
  createdDate?: string
  endingModifiedDate?: string
  employeeId?: string
  externalEntityType?: string
  externalEntityId?: string
  formName?: string
  modifiedBy?: string
  modifiedDate?: string
  orderNumber?: string
  pageNumber?: number
  pageSize?: number
  sort?: string
  sortDescending?: boolean
  startingModifiedDate?: string
  status?: string
  templateName?: string
  tenantId?: string
}

export interface GetFormsResponse {
  count: number
  items: IForm[]
  pageNumber: number
  pageSize: number
}

export interface AddFormInput {
  externalEntityId1: number
  externalEntityType1: string
  formName: string
  templateId: number
}

export interface AddFormResponse {
  formId: number
  formStatus: number
  status: 'failed' | 'success'
  statusMessage: string
}

export interface GetEmailHistoryResponse {
  records: EmailHistoryRecord[]
  totalRecords: number
}

export interface EmailHistoryRecord {
  date_cancelled: Date | null
  date_created: Date | null
  date_delivered: Date | null
  date_failed: Date | null
  date_lastClicked: Date | null
  date_lastOpened: Date | null
  date_queued: Date | null
  date_sent: Date | null
  emailAddress: string
  entityId: string
  entityType: string
  failure_detail: string | null
  id: string
  name: string
  raw_html: string
  raw_plainText: string
  raw_subject: string
}

export type FormStatusCode = 0 | 1 | 2 | 3 | 4 | 5 | 6

export type AgreementSource = 'mapview' | 'detail'

export interface AddFormPacketRequest {
  packetName?: string
  formName?: string
  templateId?: number | null
  packetId?: number | null
  formId?: number | null
  IsPacket?: boolean
  externalEntityType1?: string | null
  externalEntityId1?: string | null
  externalEntityType5?: string
  externalEntityId5?: number
  templateList?: number[]
  templateServices?: AddFormPacketTemplates[]
  agreementRenderObjects?: AgreementRenderObjects[]
  appData?: AppData[]
  source?: AgreementSource
}

export type AgreementRenderObjects = {
  renderObjectType: RenderObjectType
  isFileFromCoreApp: boolean
  id: string
  name: string | undefined
  fileSize: null | string
  fileCreatedDate: null | string | Date
  fileType?: string
  isCompletedFormPdf: boolean
}

export enum RenderObjectType {
  Template = 'Template',
  Attachment = 'Attachment',
}

export interface AddFormPacketTemplates {
  templateId: number
  dynamicServiceBlockMetadata: {
    includeDetail: boolean
    serviceIds: number[]
  }
}

export interface AppData {
  value: boolean
  key: string
}

export interface AgreementConfigs {
  formId: number
  templateId: number
  templateServices: TemplateService[]
  packetId: number | null
  isPacket: boolean
  formName: string
  agreementRenderObjects?: AgreementRenderObjects[]
}

interface TemplateService {
  templateId: number
  dynamicServiceBlockMetadata: {
    includeDetail: boolean
    serviceIds: number[]
  }
}

export interface IAgreementStatus {
  id: string
  name: string
}
